/**
 * BookingOverlapPanel — Today's Combined Booking Timeline (iter268).
 *
 * Reads /api/bookings/overlap/timeline for (center, date) and renders:
 *   • Combined timeline (table + celebration + catering)
 *   • Overlap groups with actionable buttons (Propose Common Menu,
 *     Combined Prep Plan, Alert Kitchen, Capacity Review,
 *     Acknowledge & Keep)
 * No customer booking is ever mutated from here — actions record
 * decisions or return proposals for the manager to review.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  RefreshCw, AlertTriangle, Utensils, ChefHat, Bell, Users,
  CheckCircle2,
} from "lucide-react";
import { api, fetchCentersFromDB } from "@/lib/api";

const KIND_STYLE = {
  table:       { color: "bg-sky-100 text-sky-800 border-sky-300",       label: "Table" },
  celebration: { color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", label: "Celebration" },
  catering:    { color: "bg-amber-100 text-amber-800 border-amber-300", label: "Catering" },
};

export default function BookingOverlapPanel({ session }) {
  const [centers, setCenters] = useState([]);
  const [center, setCenter] = useState(session?.center || "");
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCentersFromDB();
        setCenters(c || []);
        if (!center && c?.[0]?.code) setCenter(c[0].code);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    if (!center || !date || !session?.token) return;
    setLoading(true);
    try {
      const res = await api.post("/bookings/overlap/timeline", {
        token: session.token, center, date,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [center, date]);

  const acknowledge = async (group) => {
    const note = window.prompt(
      "Add a note for the manager's acknowledgement (optional):", "");
    if (note === null) return;
    try {
      await api.post("/bookings/overlap/acknowledge", {
        token: session.token, center, date,
        ids: group.commitments.map((c) => c.id),
        note: note || "",
      });
      toast.success("Acknowledged. Bookings remain unchanged.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record");
    }
  };

  const propose = async (group) => {
    try {
      const res = await api.post("/bookings/overlap/propose-common", {
        token: session.token, center, date,
        ids: group.commitments.map((c) => c.id),
      });
      const p = res.data.proposal || {};
      alert(
        `Common Menu Proposal\n\n${p.hint || ""}\n\n` +
        (p.common_dishes?.length
          ? `Common dishes:\n• ${p.common_dishes.join("\n• ")}`
          : "(none)"),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Proposal failed");
    }
  };

  const combinedPrep = async (group) => {
    try {
      const res = await api.post("/bookings/overlap/combined-prep", {
        token: session.token, center, date,
        ids: group.commitments.map((c) => c.id),
      });
      const lines = res.data.plan?.lines || [];
      const text = lines.slice(0, 20)
        .map((l) => `• ${l.dish}: ${l.total_guests} guests across ${l.orders.length} order(s)`)
        .join("\n") || "(no dishes to combine)";
      alert(`Combined Preparation Plan\n\n${text}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan failed");
    }
  };

  const alertKitchen = async (group) => {
    const note = window.prompt(
      "Kitchen alert note (optional):", "Two orders sharing common dishes");
    if (note === null) return;
    try {
      await api.post("/bookings/overlap/alert-kitchen", {
        token: session.token, center, date,
        ids: group.commitments.map((c) => c.id),
        note: note || "",
      });
      toast.success("Kitchen alert recorded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Alert failed");
    }
  };

  const capacityCheck = async (group) => {
    try {
      const res = await api.post("/bookings/overlap/capacity-review", {
        token: session.token, center, date,
        ids: group.commitments.map((c) => c.id),
      });
      const r = res.data.review || {};
      alert(
        `Centre Capacity Review\n\n` +
        `Total guests: ${r.total_guests}\n` +
        `Dine-in guests: ${r.dine_in_guests}\n` +
        `Seat capacity: ${r.seat_capacity ?? "not configured"}\n` +
        (r.pressure_flag ? "⚠ Pressure flag ON" : "No pressure flag") +
        (r.note ? `\n\n${r.note}` : ""),
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Review failed");
    }
  };

  const groupsCount = data?.overlap_groups?.length || 0;

  return (
    <Card data-testid="booking-overlap-panel" className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Today's Combined Booking Timeline
            {groupsCount > 0 && (
              <Badge className="ml-2 bg-amber-100 text-amber-900 border-amber-300">
                {groupsCount} overlap{groupsCount === 1 ? "" : "s"}
              </Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            <Select value={center} onValueChange={setCenter}>
              <SelectTrigger className="w-48" data-testid="bol-center-select">
                <SelectValue placeholder="Centre" />
              </SelectTrigger>
              <SelectContent>
                {(centers || []).map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40 max-w-full"
              data-testid="bol-date"
            />
            <Button size="sm" variant="outline" onClick={load} disabled={loading}
                    data-testid="bol-refresh">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Overlap groups */}
        {groupsCount === 0 ? (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">
            <CheckCircle2 className="w-4 h-4 inline mr-1" />
            No overlapping commitments for this centre / date.
          </div>
        ) : (
          data.overlap_groups.map((g, gi) => (
            <div
              key={`${g.start}-${gi}`}
              className="border-l-4 border-amber-500 bg-amber-50/50 rounded p-3 space-y-2"
              data-testid={`bol-group-${gi}`}
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-semibold text-amber-900">
                    Overlap {g.start} → {g.end} · {g.count} commitment{g.count === 1 ? "" : "s"}
                    · {g.total_guests} total guests
                  </div>
                  <div className="text-xs text-amber-800">
                    Overlapping bookings detected — please review the menu,
                    kitchen preparation, staffing and service arrangements.
                  </div>
                  {g.table_service_conflict && (
                    <div className="text-xs text-red-700 mt-1">
                      ⚠ Multiple dine-in commitments — check table availability.
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" onClick={() => propose(g)}
                          data-testid={`bol-propose-${gi}`}>
                    <Utensils className="w-3 h-3 mr-1" />
                    Propose Common Menu
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => combinedPrep(g)}
                          data-testid={`bol-prep-${gi}`}>
                    <ChefHat className="w-3 h-3 mr-1" />
                    Combined Prep Plan
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => alertKitchen(g)}
                          data-testid={`bol-alert-${gi}`}>
                    <Bell className="w-3 h-3 mr-1" />
                    Alert Kitchen
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => capacityCheck(g)}
                          data-testid={`bol-capacity-${gi}`}>
                    <Users className="w-3 h-3 mr-1" />
                    Review Capacity
                  </Button>
                  <Button size="sm" onClick={() => acknowledge(g)}
                          data-testid={`bol-ack-${gi}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Keep All
                  </Button>
                </div>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-white/60">
                    <th className="text-left p-1">Type</th>
                    <th className="text-left p-1">Time</th>
                    <th className="text-right p-1">Guests</th>
                    <th className="text-left p-1">Menu</th>
                    <th className="text-left p-1">Service</th>
                    <th className="text-left p-1">Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {g.commitments.map((c) => {
                    const s = KIND_STYLE[c.kind] || {};
                    return (
                      <tr key={c.id} className="border-b hover:bg-white"
                          data-testid={`bol-commit-${c.id}`}>
                        <td className="p-1">
                          <Badge variant="outline" className={s.color}>
                            {s.label || c.kind}
                          </Badge>
                        </td>
                        <td className="p-1 font-mono text-xs">
                          {c.start && c.end ? `${c.start}–${c.end}` : (c.time_slot || "—")}
                          {c.unknown_duration && (
                            <span className="ml-1 text-red-600" title="Duration not known — flag for review">⚑</span>
                          )}
                        </td>
                        <td className="p-1 text-right">{c.guests}</td>
                        <td className="p-1 max-w-[240px] truncate" title={c.menu_summary}>
                          {c.menu_summary || "—"}
                        </td>
                        <td className="p-1 text-xs">{c.service_requirement}</td>
                        <td className="p-1 text-xs">{c.customer || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {g.common_menu_hint?.count > 0 && (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                  <strong>Common dishes:</strong>{" "}
                  {g.common_menu_hint.common_dishes.slice(0, 6).join(", ")}
                  {g.common_menu_hint.common_dishes.length > 6 && " …"}
                </div>
              )}
            </div>
          ))
        )}

        {/* Full timeline */}
        {data?.commitments?.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-sm font-semibold">
              Full timeline ({data.commitments.length} commitment{data.commitments.length === 1 ? "" : "s"})
            </summary>
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-1">Type</th>
                  <th className="text-left p-1">Time</th>
                  <th className="text-right p-1">Guests</th>
                  <th className="text-left p-1">Customer</th>
                  <th className="text-left p-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.commitments.map((c) => {
                  const s = KIND_STYLE[c.kind] || {};
                  return (
                    <tr key={c.id} className="border-b">
                      <td className="p-1">
                        <Badge variant="outline" className={s.color}>{s.label || c.kind}</Badge>
                      </td>
                      <td className="p-1 font-mono text-xs">{c.start && c.end ? `${c.start}–${c.end}` : (c.time_slot || "—")}</td>
                      <td className="p-1 text-right">{c.guests}</td>
                      <td className="p-1 text-xs">{c.customer || "—"}</td>
                      <td className="p-1 text-xs">{c.status || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
